'use client';

import { c as _c } from "react/compiler-runtime";
import { useEarlyStepsEventTracking } from '@app/registration/(with-padding)/_basic/useEarlyStepsEventTracking';
import { FormikTextInput } from '@components/FormikTextInput';
import { preferredAvatarSizeFormat } from '@components/MemberAvatar/preferredAvatarSizeFormat';
import StepHeader from '@components/Registration/StepHeader';
import { Avatar, Flag, IDPictogram, Typography } from '@internations/ds';
import { getPlaceholderProfileImageUrl } from '@routes/utils/getPlaceholderProfileImageUrl';
import { TRACKING_TYPE_REGISTRATION_STARTED } from '@tracking/events';
import { formatImageUrl } from '@util/assets';
import createPasswordLabel from '@util/labels/createPassword';
import createYourAccountLabel from '@util/labels/createYourAccount';
import emailLabel from '@util/labels/email';
import firstNameLabel from '@util/labels/firstName';
import lastNameLabel from '@util/labels/lastName';
import profileBornInLabel from '@util/labels/profileBornIn';
import profileLivingInLabel from '@util/labels/profileLivingIn';
import repeatPasswordLabel from '@util/labels/repeatPassword';
import youAreInvitedByLabel from '@util/labels/youAreInvitedBy';
import { maxLengthName } from '@util/registration/BasicFormModel';
import { ReactElement, useRef } from 'react';
type InviterCardProps = {
  inviter: TMember;
};
function InviterCard(t0) {
  const $ = _c(36);
  const {
    inviter
  } = t0;
  const origin = inviter.origin.country;
  const residency = inviter.residency.country;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Typography variant="caption" className="color-text-secondary" gutterBottom={true}>{youAreInvitedByLabel}</Typography>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== inviter.gender || $[2] !== inviter.imagePath) {
    t2 = inviter.imagePath ? formatImageUrl(inviter.imagePath, "large" ? preferredAvatarSizeFormat.large : "120_120") : getPlaceholderProfileImageUrl(inviter.gender);
    $[1] = inviter.gender;
    $[2] = inviter.imagePath;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <Avatar size="large" alt="image" src={t2} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== inviter.firstName || $[7] !== inviter.lastName) {
    t4 = <Typography variant="h3" className="color-primary-main">{inviter.firstName} {inviter.lastName}</Typography>;
    $[6] = inviter.firstName;
    $[7] = inviter.lastName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== origin.iocCode || $[10] !== origin.name) {
    t5 = <span className="mr-1"><Flag code={origin.iocCode} title={origin.name} /></span>;
    $[9] = origin.iocCode;
    $[10] = origin.name;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== inviter) {
    t6 = profileBornInLabel(inviter);
    $[12] = inviter;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <Typography variant="caption" className="inline color-text-secondary">{t6}</Typography>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== residency.iocCode || $[20] !== residency.name) {
    t9 = <span className="mr-1"><Flag code={residency.iocCode} title={residency.name} /></span>;
    $[19] = residency.iocCode;
    $[20] = residency.name;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== inviter) {
    t10 = profileLivingInLabel(inviter);
    $[22] = inviter;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <Typography variant="caption" className="inline color-text-secondary">{t10}</Typography>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t9) {
    t12 = <div>{t9}{t11}</div>;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t4 || $[31] !== t8) {
    t13 = <div className="flex flex-col">{t4}{t8}{t12}</div>;
    $[29] = t12;
    $[30] = t4;
    $[31] = t8;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13 || $[34] !== t3) {
    t14 = <div className="m-auto" data-testid="inviterCard">{t1}<div className="flex flex-row items-center gap-1">{t3}{t13}</div></div>;
    $[33] = t13;
    $[34] = t3;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}
type FirstScreenProps = {
  inviter?: TMember;
  isEmailReadOnly?: boolean;
};
const changeEmailNotice = 'Once you complete your registration, you will be able to change your email address at any time.';
function FirstScreen(t0) {
  const $ = _c(18);
  const {
    inviter,
    isEmailReadOnly
  } = t0;
  const {
    mutateAsync: trackEvent
  } = useEarlyStepsEventTracking();
  const registrationStartedEventTrackedRef = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <StepHeader Pictogram={IDPictogram} heading={createYourAccountLabel} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== inviter) {
    t2 = inviter ? <div className="flex items-center"><InviterCard inviter={inviter} /></div> : null;
    $[1] = inviter;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col sm:flex-row gap-2"><FormikTextInput name="firstName" label={firstNameLabel} data-testid="firstName" autoComplete="given-name" maxLength={maxLengthName} /><FormikTextInput name="lastName" label={lastNameLabel} data-testid="lastName" autoComplete="family-name" maxLength={maxLengthName} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isEmailReadOnly) {
    t4 = <FormikTextInput name="email" label={emailLabel} type="email" data-testid="email" autoComplete="email" readOnly={isEmailReadOnly} disabled={isEmailReadOnly} />;
    $[4] = isEmailReadOnly;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== inviter) {
    t5 = inviter ? <Typography variant="subtitle2" component="p" data-testid="changeEmailNotice" className="pt-2 color-text-secondary"><em>{changeEmailNotice}</em></Typography> : null;
    $[6] = inviter;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== trackEvent) {
    t6 = <FormikTextInput type="password" name="password" label={createPasswordLabel} data-testid="password" autoComplete="new-password" onFocus={() => {
      if (!registrationStartedEventTrackedRef.current) {
        trackEvent({
          eventName: TRACKING_TYPE_REGISTRATION_STARTED
        }, {
          onSuccess: () => {
            registrationStartedEventTrackedRef.current = true;
          }
        });
      }
    }} />;
    $[8] = trackEvent;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FormikTextInput type="password" name="repeatPassword" label={repeatPasswordLabel} data-testid="repeatPassword" autoComplete="new-password" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t8 = <div className="flex flex-col gap-2.5">{t3}{t4}{t5}{t6}{t7}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t2 || $[16] !== t8) {
    t9 = <>{t1}{t2}{t8}</>;
    $[15] = t2;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}
export default FirstScreen;